import { useCallback, useEffect, useState } from 'react';
import InputPage from './pages/InputPage.jsx';
import RevealPage from './pages/RevealPage.jsx';
import PlayPage from './pages/PlayPage.jsx';
import ResultPage from './pages/ResultPage.jsx';
import { loadRun, saveRun, clearRun, newSeed, loadLives, archiveRun, replayBaseline, runIdentity, newRunId, takeStorageNotice } from './lib/storage.js';
import { createRun } from './lib/engine.js';
import { analyzeSaju, INITIAL_STATS_VERSION } from './api/client.js';
import Desktop from './components/Desktop.jsx';

// 엔진 형식이 아닌 옛 저장 데이터(개발 중 버전)는 버린다
function loadValidRun() {
  const r = loadRun();
  if (r && r.stats && r.momentum && r.saju?.daewoon && Array.isArray(r.log)) {
    r.people ??= []; r.meetAge ??= -1;   // 곁의 사람 도입 전 저장본
    return r;
  }
  if (r) clearRun();
  return null;
}

// 저장 중 오래된 보관 기록을 지웠다면 안내 문구를 돌려준다(없으면 빈 문자열).
function storageNoticeText() {
  const notice = takeStorageNotice();
  return notice ? `저장 공간이 부족해 가장 오래된 완료 기록 ${notice.droppedLives}개를 지웠습니다. 같은 조건 비교에 쓰는 기록은 지우지 않았습니다.` : '';
}

function Application() {
  const [run, setRun] = useState(() => loadValidRun());
  const [screen, setScreen] = useState(() => {
    const r = loadValidRun();
    return !r ? 'input' : r.age > 0 || r.status === 'ended' ? 'play' : 'reveal';
  });
  const [refresh, setRefresh] = useState({ loading: false, error: '' });
  const [refreshAttempt, setRefreshAttempt] = useState(0);
  const [lives, setLives] = useState(() => loadLives());
  const [archiveError, setArchiveError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [storageNotice, setStorageNotice] = useState('');   // 저장 공간이 모자라 오래된 보관 기록을 지웠다는 안내

  const update = useCallback((next) => {
    const saved = saveRun(next);
    const notice = storageNoticeText();
    if (notice) setStorageNotice(notice);
    setSaveError(saved ? '' : '진행 기록을 저장하지 못했습니다. 현재 화면은 유지되지만 새로고침하면 최근 진행이 남지 않을 수 있습니다.');
    setRun(next);
  }, []);
  const needsInitialRefresh = !!run && run.age === 0 && run.history.length === 0
    && !run.replayBaseline && run.saju.statBasis?.version !== INITIAL_STATS_VERSION;

  useEffect(() => {
    if (run?.status !== 'ended') return;
    const archived = archiveRun(run);
    const notice = storageNoticeText();
    if (notice) setStorageNotice(notice);
    setArchiveError(archived ? '' : '완료 기록을 저장하지 못했습니다. 브라우저 저장 공간을 확인해 주세요. 현재 결과는 유지됩니다.');
    if (archived) setLives(loadLives());
  }, [run]);

  useEffect(() => {
    if (!needsInitialRefresh) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    setRefresh({ loading: true, error: '' });
    const refreshInitial = async () => {
      try {
        if (!run.birth) throw new Error('저장된 생년월일이 없어 초기 능력치를 갱신할 수 없습니다. 처음부터 다시 입력해 주세요.');
        const saju = await analyzeSaju(run.birth, { signal: controller.signal });
        if (cancelled) return;
        const fresh = createRun({ seed: run.seed, birth: run.birth, saju });
        // 아직 한 해도 진행하지 않은 출발값만 교체한다. NPC와 그 밖의 저장 상태는 보존한다.
        update({ ...fresh, ...run, saju: fresh.saju, stats: fresh.stats,
          birthYear: fresh.birthYear, deathAge: fresh.deathAge, balanceVersion: fresh.balanceVersion });
        setRefresh({ loading: false, error: '' });
      } catch (error) {
        if (!cancelled && error.name !== 'AbortError') {
          setRefresh({ loading: false, error: error.message || '초기 능력치를 갱신하지 못했습니다.' });
        }
      }
    };
    refreshInitial();
    return () => { cancelled = true; controller.abort(); };
  }, [needsInitialRefresh, run, update, refreshAttempt]);

  const start = (birth, saju) => {
    setStorageNotice('');
    update({ ...createRun({ seed: newSeed(), birth, saju }), runId: newRunId() });
    setScreen('reveal');
  };
  const preserveCompleted = () => {
    const archived = archiveRun(run);
    const notice = storageNoticeText();
    if (notice) setStorageNotice(notice);
    if (!archived) {
      setArchiveError('완료 기록을 저장하지 못해 새 인생으로 이동하지 않았습니다. 브라우저 저장 공간을 확인해 주세요.');
      return false;
    }
    setArchiveError('');
    setLives(loadLives());
    return true;
  };
  const reset = () => {
    if (run?.status === 'ended' && !preserveCompleted()) return;
    clearRun(); setRun(null); setScreen('input');
  };
  const replay = () => {
    if (run?.status !== 'ended' || !preserveCompleted()) return;
    const baseline = replayBaseline(run);
    const fresh = createRun({ seed: baseline.seed, birth: structuredClone(baseline.birth), saju: structuredClone(baseline.saju) });
    // 기존 인생은 보관하고 같은 출생 조건으로 새 진행을 만든다. 이전 선택·관계는 옮기지 않는다.
    const metadata = Object.fromEntries(['balanceVersion', 'rulesVersion', 'engineRulesVersion', 'engineVersion', 'eventCatalogVersion', 'initialStatsVersion', 'revisions', 'referenceData']
      .filter((key) => baseline[key] != null).map((key) => [key, structuredClone(baseline[key])]));
    update({ ...fresh, ...metadata, runId: newRunId(), deathAge: baseline.deathAge,
      replayBaseline: baseline, comparisonRunId: runIdentity(run), replayOfRunId: runIdentity(run) });
    setScreen('reveal');
  };

  if (screen === 'input' || !run) return <InputPage onSubmit={start} />;
  if (needsInitialRefresh) return (
    <div className="card narrow">
      <h1>초기 능력치 갱신</h1>
      {refresh.error
        ? <p className="error" role="alert">{refresh.error}</p>
        : <p className="muted" role="status">새 기준으로 초기 능력치를 갱신 중입니다.</p>}
      <div className="row">
        {refresh.error && <button className="primary" onClick={() => setRefreshAttempt((n) => n + 1)}>다시 계산</button>}
        <button onClick={reset}>처음부터</button>
      </div>
    </div>
  );
  if (screen === 'reveal') return <>{saveError && <p className="error" role="alert">{saveError}</p>}<RevealPage run={run} onStart={() => setScreen('play')} onReset={reset} /></>;
  if (run.status === 'ended') return <ResultPage run={run} onReset={reset} onReplay={replay} lives={lives} storageError={archiveError} storageNotice={storageNotice} />;
  return <>{saveError && <p className="error" role="alert">{saveError}</p>}{storageNotice && <p className="muted" role="status">{storageNotice}</p>}<PlayPage run={run} onChange={update} onEnd={() => {}} /></>;
}

export default function App() {
  return <Desktop><Application /></Desktop>;
}
